import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";

const UploadedDocument = forwardRef(({ lbaId, documents, onRefresh }, ref) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Replace with your actual API URL
  const API_URL = process.env.REACT_APP_API_URL;
 

  // Clear error when lbaId or documents change
  useEffect(() => {
    setError(null);
  }, [lbaId, documents]);

  const handleDownload = async (fileIndex, fileName) => {
    if (!lbaId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/official_documents/file/${lbaId}/${fileIndex}`);
      
      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download file');
    }
  };

  const handleDelete = async (fileIndex) => {
    if (!lbaId) return;
    
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/official_documents/file/${lbaId}/${fileIndex}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        // Refresh the documents list by calling the parent's refresh function
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error('Failed to delete file');
      }
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete file');
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'assets/pdf.svg';
      case 'doc':
      case 'docx':
        return 'assets/document.svg';
      case 'png':
      case 'jpg':
      case 'jpeg':
        return 'assets/image.svg';
      default:
        return 'assets/document.svg';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  // Show loading only when explicitly refreshing
  if (loading) {
    return (
      <div className="relative shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-xl border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-center justify-center p-8 text-center text-neutrals-neutral-500 font-open-sans">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-primary-500 mb-4"></div>
        <div>Refreshing documents...</div>
      </div>
    );
  }


  // Use documents passed from parent component
  if (!documents || !documents.files || documents.files.length === 0) {
    return (
      <div className="relative shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-xl border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-center justify-center p-8 text-center text-neutrals-neutral-500 font-open-sans">
        <div className="w-16 h-16 bg-neutrals-neutral-100 rounded-full flex items-center justify-center mb-4">
          <img src="assets/document.svg" alt="No documents" className="w-8 h-8" />
        </div>
        <div>No documents uploaded for this debtor yet</div>
        <div className="text-xs text-neutrals-neutral-400 mt-2">
          
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {documents.files.map((file, index) => (
          <div key={`${lbaId}-${file.index}-${index}`} className="relative shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-xl border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-start justify-start text-left text-base text-neutrals-neutral-900 font-open-sans min-w-0">
            <div className="w-full rounded-t-xl rounded-b-none bg-white box-border flex flex-col items-start justify-start p-3 gap-4">
              <div className="self-stretch relative h-[150px] bg-neutrals-neutral-50 rounded-lg flex items-center justify-center">
                <img 
                  className="w-12 h-12 object-contain opacity-50" 
                  alt={file.fileName} 
                  src={getFileIcon(file.fileName)} 
                />
              </div>
              <div className="self-stretch flex flex-col items-start justify-start gap-1.5 min-w-0">
                <div className="self-stretch relative font-semibold truncate text-sm" title={file.fileName}>
                  {file.fileName}
                </div>
                <div className="self-stretch flex justify-between items-center gap-2">
                  <div className="relative text-[10px] text-neutrals-neutral-300 truncate">
                    {formatDate(documents.createdAt || new Date().toISOString())}
                  </div>
                  <div className="relative text-[10px] text-neutrals-neutral-400 flex-shrink-0">
                    {formatFileSize(file.fileSize)}
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full border-neutrals-neutral-100 border-solid border-t-[1px] box-border flex flex-row items-stretch justify-between py-3 px-2 text-xs gap-1">
              <button
                onClick={() => handleDownload(file.index, file.fileName)}
                className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-[8px] bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-center justify-center py-2 px-1.5 hover:bg-neutrals-neutral-50 transition-colors flex-1"
              >
                <div className="flex flex-row items-center justify-center gap-1">
                  <div className="w-4 h-4 flex-shrink-0">
                    <img 
                      className="w-full h-full object-contain" 
                      alt="" 
                      src="assets/download.svg" 
                    />
                  </div>
                  <div className="relative font-semibold whitespace-nowrap">Download</div>
                </div>
              </button>
              <button
                onClick={() => handleDelete(file.index)}
                className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-[8px] bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-center justify-center py-2 px-1.5 hover:bg-red-50 hover:border-red-200 transition-colors flex-1"
              >
                <div className="flex flex-row items-center justify-center gap-1">
                  <div className="w-4 h-4 flex-shrink-0">
                    <img 
                      className="w-full h-full object-contain" 
                      alt="" 
                      src="assets/delete.svg" 
                    />
                  </div>
                  <div className="relative font-semibold text-red-600 whitespace-nowrap">Delete</div>
                </div>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

UploadedDocument.displayName = 'UploadedDocument';

export default UploadedDocument;